import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CreateShareInvitationRequest, InvitationsService, Org } from 'ldt-identity-service-api';
import { AuthService } from 'src/app/auth/service/auth.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { InvitationsCreateDialogComponent } from 'src/app/team/invitations-create-dialog/invitations-create-dialog.component';
import { TeamInvitationsComponent } from 'src/app/team/team-invitations/team-invitations.component';
import { HostnameService } from 'src/app/hostname-router-service';
import { Observable } from 'rxjs';
import { WorkforceLayoutService } from '../workforce-layout.service';
import { WorkforceAppComponent } from '../app.wf.component';

@Component({
  selector: 'app-workforce-team',
  templateUrl: './team.component.html',
  styleUrls: ['./team.component.scss'],
  standalone: false
})
export class WorkforceTeamComponent implements OnInit {
  @ViewChild(TeamInvitationsComponent) inviteComponent: TeamInvitationsComponent;

  isSmallScreen$: Observable<boolean>;
  org: Org;
  canAdmin: boolean = false;

  // Member-cap state. maxUsers is the org's identity-level seat cap ('unlimited',
  // a number, or unset). memberCount comes from the user list once it loads.
  maxUsers: number | 'unlimited' | null = null;
  memberCount: number | null = null;

  constructor(
    private router: Router,
    private auth: AuthService,
    private invService: InvitationsService,
    public dialog: MatDialog,
    private notifyService: NotificationService,
    private layoutService: WorkforceLayoutService,
    private parent: WorkforceAppComponent
  ) {
    this.isSmallScreen$ = this.layoutService.isSmallScreen$;
  }

  toggleThinSidenav() {
    this.parent.toggleThinSidenav();
  }

  ngOnInit() {
    const thisOrg = this.auth.getSelectedOrg();
    if (!thisOrg) {
      this.router.navigateByUrl('/insights');
    } else {
      this.org = thisOrg;
      // maxUsers lives under the identity block now; -1 (or no value) = unlimited.
      const rawMaxUsers = thisOrg.settings?.identity?.maxUsers;
      this.maxUsers =
        rawMaxUsers === undefined || rawMaxUsers === null || rawMaxUsers === -1
          ? 'unlimited'
          : rawMaxUsers;
    }
    this.canAdmin = this.auth.userHasRole('admin');
  }

  onMemberCount(count: number): void {
    this.memberCount = count;
  }

  // Plan changes go through the team for now — open Intercom instead of the
  // (hidden) subscription picker.
  showPlansDialog(): void {
    (window as any)['Intercom']('showNewMessage');
  }

  // True only when the org has a finite seat cap (not unlimited / unset).
  get hasSeatLimit(): boolean {
    return typeof this.maxUsers === 'number';
  }

  get seatsRemaining(): number | null {
    if (typeof this.maxUsers !== 'number' || this.memberCount === null) {
      return null;
    }
    return Math.max(0, this.maxUsers - this.memberCount);
  }

  get atSeatCap(): boolean {
    return this.seatsRemaining !== null && this.seatsRemaining <= 0;
  }

  showInviteDialog(evt?: MouseEvent) {
    evt?.stopPropagation();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '500px';

    const dialogRef = this.dialog.open(InvitationsCreateDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((data) => {
      if (data && data.emails && Array.isArray(data.emails)) {
        const product = HostnameService.isWorkforceHostname() ? 'workforce' : 'gotlivedata';
        data.emails.forEach((email: string) => {
          const req: CreateShareInvitationRequest = {
            invitee_email: email.trim(),
            invited_by_name: this.auth.getUserValue.name,
            role: data.role,
          };

          this.invService
            .createShareInvitation(product, this.auth.getSelectedOrgIdValue, req)
            .subscribe({
              next: (r) => {
                this.notifyService.success(email + ' successfully invited.');
                if (this.inviteComponent) {
                  this.inviteComponent.refreshOrgInvitations();
                }
              },
              error: (err) => {
                if (err.status && err.status === 409) {
                  this.notifyService.error('User already has access to this org');
                } else {
                  this.notifyService.error(
                    'Failed to invite ' + email + '. Please try again later.'
                  );
                }
              },
            });
        });
      }
    });
  }
}
